import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiGithub } from 'react-icons/fi';
import settings from '../../data/settings.json';
import { cn } from '../../utils/helpers';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = settings.navigation;

  return (
    <>
    <header className={cn(
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      scrolled
        ? 'bg-white/95 backdrop-blur-md shadow-soft border-b border-slate-100'
        : 'bg-transparent'
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-600 to-accent-600 flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm">mk</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-slate-900 text-sm leading-none block">Malintha Kavinda</span>
              <span className="text-xs text-slate-500 leading-none block mt-0.5">Full Stack Developer</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'text-primary-600 bg-primary-50 font-semibold'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  )}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://github.com/kavindarlm"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all"
            >
              <FiGithub className="w-5 h-5" />
            </a>
            <Link
              to="/contact"
              className="btn-primary text-sm py-2 px-4"
            >
              Hire Me
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <FiX className="w-5 h-5" /> : <FiMenu className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </header>

      {/* Mobile menu — outside header to avoid nested fixed/z-index stacking issues */}
      <div className={cn(
        'fixed inset-0 top-[72px] bg-white z-[60] md:hidden transition-all duration-300 ease-in-out overflow-y-auto',
        mobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
      )}>
        <div className="px-4 pt-4 pb-8 space-y-1 border-t border-slate-100">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  'flex items-center px-4 py-3 rounded-xl text-base font-medium transition-all',
                  isActive
                    ? 'text-primary-600 bg-primary-50 font-semibold'
                    : 'text-slate-700 hover:bg-slate-50'
                )}
              >
                {link.label}
              </Link>
            );
          })}

          <div className="pt-4 mt-4 border-t border-slate-100 space-y-3">
            <a
              href="https://github.com/kavindarlm"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <FiGithub className="w-5 h-5" />
              GitHub
            </a>
            <Link to="/contact" className="btn-primary w-full justify-center">
              Hire Me
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
